import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, ImagePlus, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { api, formatApiError } from "../../lib/api";
import { Badge, PanelTitle, VERIFICATION_LABELS, fmtDateTime, shortHash } from "./ui";

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    img.src = url;
  });

export const WalletBox = ({ wallet, token }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(wallet);
    setCopied(true);
    toast.success("Dirección copiada");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-5" data-testid={`wallet-box-${token}`}>
      <div className="flex items-center justify-between">
        <p className="font-display text-lg font-semibold">{token}</p>
        <span className="rounded-full bg-[#8247E5]/10 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-[#8247E5]">Red Polygon</span>
      </div>
      <div className="mt-4 flex items-start gap-4">
        <div className="shrink-0 rounded-xl border border-ink/10 p-2"><QRCodeSVG value={wallet} size={96} fgColor="#0A1230" /></div>
        <div className="min-w-0 flex-1">
          <p className="break-all font-mono text-xs leading-relaxed" data-testid={`wallet-address-${token}`}>{wallet}</p>
          <button onClick={copy} className="mt-3 inline-flex items-center gap-2 rounded-full border border-ink/15 px-3.5 py-2 text-xs font-semibold hover:bg-paper" data-testid={`wallet-copy-${token}`}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copiar dirección
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentRow = ({ p, onReverify, busy }) => (
  <div className="rounded-2xl border border-ink/10 bg-white p-5" data-testid={`payment-item-${p.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="font-semibold">{p.token} · <span className="font-mono text-xs" title={p.tx_hash}>{shortHash(p.tx_hash)}</span></p>
        <p className="mt-1 text-xs text-ink/60">Enviado {fmtDateTime(p.created_at)}{p.reviewed_at && ` · Revisado ${fmtDateTime(p.reviewed_at)}`}</p>
      </div>
      <Badge status={p.status} testId={`payment-status-${p.id}`} />
    </div>
    {p.verification && (
      <p className={`mt-3 text-sm ${p.verification.verified ? "text-leaf-deep" : "text-ink/75"}`} data-testid={`payment-verification-${p.id}`}>
        {VERIFICATION_LABELS[p.verification.reason] || p.verification.reason}
        {p.verification.amount != null && ` · Monto detectado: ${p.verification.amount} ${p.token}`}
      </p>
    )}
    {p.review_note && <p className="mt-2 text-sm text-ink/75">Nota del administrador: {p.review_note}</p>}
    {p.status === "pendiente" && (
      <button onClick={() => onReverify(p.id)} disabled={busy} className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-leaf-deep underline-offset-4 hover:underline disabled:opacity-60" data-testid={`payment-reverify-${p.id}`}>
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Volver a verificar en blockchain
      </button>
    )}
  </div>
);

export const MembershipPanel = ({ data, onActivated }) => {
  const { membership, profile } = data;
  const active = profile.membership_status === "activa";
  const [payments, setPayments] = useState([]);
  const [token, setToken] = useState("USDC");
  const [txHash, setTxHash] = useState("");
  const [proof, setProof] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/me/payments").then(({ data: d }) => setPayments(d));
  useEffect(() => {
    load();
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Sube una imagen (captura de pantalla).");
    setProof(await fileToDataUrl(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: p } = await api.post("/me/payments", { token, tx_hash: txHash.trim(), proof_image: proof });
      setTxHash("");
      setProof("");
      if (fileRef.current) fileRef.current.value = "";
      if (p.verification?.verified) toast.success("Pago verificado en blockchain. El administrador lo confirmará para activar tu membresía.");
      else toast.info("Recibimos tu comprobante. El administrador lo revisará y confirmará tu membresía.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reverify = async (id) => {
    setBusyId(id);
    try {
      const { data: p } = await api.post(`/me/payments/${id}/verify`);
      toast.info(VERIFICATION_LABELS[p.verification?.reason] || "Aún no se puede verificar.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div data-testid="membership-panel">
      <PanelTitle title="Membresía Dorada" text={`Pago único de $${membership.price} USD en USDC o USDT por la red Polygon. Envía exactamente el monto a la dirección indicada y después registra el hash de la transacción.`} />
      {active ? (
        <div className="mt-6 flex items-center gap-4 rounded-2xl border border-leaf/40 bg-leaf/10 p-6" data-testid="membership-active-box">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-leaf text-ink"><ShieldCheck className="h-6 w-6" /></span>
          <div>
            <p className="font-display text-xl font-semibold">Tu Membresía Dorada está activa</p>
            <p className="mt-1 text-sm text-ink/75">Activada el {fmtDateTime(profile.membership_paid_at)}. Ya puedes cobrar las comisiones de tu red.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <WalletBox wallet={membership.wallet} token="USDC" />
            <WalletBox wallet={membership.wallet} token="USDT" />
          </div>
          <p className="mt-3 text-xs text-ink/60">Importante: envía únicamente USDC o USDT por la red Polygon. Transferencias por otras redes no podrán verificarse.</p>

          <form onSubmit={submit} className="mt-8 rounded-[1.5rem] border border-ink/10 bg-white p-6" data-testid="payment-form">
            <p className="font-display text-xl font-semibold">Registrar mi pago</p>
            <div className="mt-5 grid gap-5 sm:grid-cols-[160px_1fr]">
              <div>
                <label className="mb-2 block text-sm font-semibold">Moneda enviada</label>
                <div className="flex gap-2">
                  {["USDC", "USDT"].map((t) => (
                    <button key={t} type="button" onClick={() => setToken(t)} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold ${token === t ? "border-ink bg-ink text-white" : "border-ink/15 hover:bg-paper"}`} data-testid={`payment-token-${t}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="tx" className="mb-2 block text-sm font-semibold">Hash de la transacción (Txn Hash)</label>
                <Input id="tx" required pattern="^0x[0-9a-fA-F]{64}$" placeholder="0x…" value={txHash} onChange={(e) => setTxHash(e.target.value)} className="h-11 bg-white font-mono text-xs" data-testid="payment-txhash" />
                <p className="mt-1.5 text-xs text-ink/60">Lo encuentras en tu wallet o exchange, en el detalle de la transacción enviada.</p>
              </div>
            </div>
            <div className="mt-5">
              <label className="mb-2 block text-sm font-semibold">Comprobante (captura de pantalla, opcional)</label>
              <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-ink/25 px-4 py-3 text-sm text-ink/70 hover:bg-paper">
                <ImagePlus className="h-5 w-5" /> {proof ? "Imagen lista para enviar" : "Seleccionar imagen"}
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="payment-proof-input" />
              </label>
              {proof && <img src={proof} alt="Comprobante" className="mt-3 h-32 rounded-xl border border-ink/10 object-cover" data-testid="payment-proof-preview" />}
            </div>
            <button type="submit" disabled={loading} className="btn-leaf mt-6 disabled:opacity-60" data-testid="payment-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />} Enviar y verificar en blockchain
            </button>
          </form>
        </>
      )}

      {payments.length > 0 && (
        <div className="mt-8">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink/60">Historial de comprobantes</p>
          <div className="mt-3 space-y-3" data-testid="payments-history">
            {payments.map((p) => <PaymentRow key={p.id} p={p} onReverify={reverify} busy={busyId === p.id} />)}
          </div>
        </div>
      )}
    </div>
  );
};
